import type { SupabaseClient } from '../../lib/supabase';

/**
 * Thumbs up / down on an agent's message (Messaging Drop 3b-2,
 * plan-drop3-extras.md F2-F4), stored in public.message_feedback
 * (20270181000100). One grade per person per message.
 *
 * Visible to the room (Kyu, 26 Sept 11:35): everyone who can see the message
 * sees the up and down counts, who gave them, and every "What was wrong?"
 * note, like reactions. The database decides who can read the rows (the
 * message_feedback select policy follows the comment's room, the same test
 * reactions use); nothing here is trusted for permission. The page reads
 * every row on the open thread's agent messages and works out which one is
 * the signed-in person's own by user_id, so somebody else's grade is never
 * taken for yours.
 *
 * Writes send only comment_id, grade and reason. user_id defaults to the
 * caller, and id / created_at / updated_at come from the database; sending
 * them is refused (fix-drop3b2-db-dev4.patch).
 */

export type Grade = 'up' | 'down';

export interface MyGrade {
  grade: Grade;
  reason: string | null;
}

/** message_feedback_reason_length. */
export const FEEDBACK_REASON_MAX = 300;

export interface FeedbackRow {
  comment_id: string;
  user_id: string;
  grade: string;
  reason: string | null;
  created_at: string;
}

/** Everything the room sees about one agent message's grades. */
export interface MessageFeedback {
  /** Who gave a thumbs up, by user id, oldest first. Includes you. */
  up: string[];
  /** Who gave a thumbs down, by user id, oldest first. Includes you. */
  down: string[];
  /** Every non-empty "What was wrong?" line, oldest first. Includes yours. */
  notes: { userId: string; reason: string }[];
  /** The signed-in person's own grade, found by user_id. */
  mine: MyGrade | undefined;
}

/**
 * Rows grouped per message, oldest first so names do not jump around.
 * "mine" is only ever the row whose user_id is the signed-in person.
 */
export function summarizeFeedback(rows: FeedbackRow[], myId: string): Map<string, MessageFeedback> {
  const ordered = [...rows].sort((a, b) => a.created_at.localeCompare(b.created_at));
  const out = new Map<string, MessageFeedback>();
  for (const row of ordered) {
    if (row.grade !== 'up' && row.grade !== 'down') {
      continue;
    }
    const entry = out.get(row.comment_id) ?? { up: [], down: [], notes: [], mine: undefined };
    entry[row.grade].push(row.user_id);
    const reason = row.reason?.trim() ?? '';
    if (reason !== '') {
      entry.notes.push({ userId: row.user_id, reason });
    }
    if (myId !== '' && row.user_id === myId) {
      entry.mine = { grade: row.grade, reason: row.reason };
    }
    out.set(row.comment_id, entry);
  }
  return out;
}

export async function fetchFeedback(
  client: SupabaseClient,
  commentIds: string[],
  myId: string,
): Promise<Map<string, MessageFeedback>> {
  if (commentIds.length === 0) {
    return new Map();
  }
  // Chunked like fetchReactions, so a long agent thread stays under the URL
  // limit (Dev4, t_8f7714ba).
  const rows: FeedbackRow[] = [];
  for (let at = 0; at < commentIds.length; at += FEEDBACK_IN_CHUNK) {
    const { data, error } = await client
      .from('message_feedback')
      .select('comment_id, user_id, grade, reason, created_at')
      .in('comment_id', commentIds.slice(at, at + FEEDBACK_IN_CHUNK));
    if (error) {
      throw new Error(error.message);
    }
    rows.push(...(Array.isArray(data) ? data : []));
  }
  return summarizeFeedback(rows, myId);
}

/** How many comment ids go into one `in (...)` filter. */
export const FEEDBACK_IN_CHUNK = 200;

/**
 * What a click on a thumb does, given the grade already there (F2):
 * no grade: add it; the same thumb again: remove it; the other thumb: change it.
 */
export type FeedbackAction =
  { kind: 'insert'; grade: Grade } | { kind: 'update'; grade: Grade } | { kind: 'delete' };

export function feedbackActionFor(current: MyGrade | undefined, clicked: Grade): FeedbackAction {
  if (current === undefined) {
    return { kind: 'insert', grade: clicked };
  }
  if (current.grade === clicked) {
    return { kind: 'delete' };
  }
  return { kind: 'update', grade: clicked };
}

function cleanReason(reason: string | null): string | null {
  if (reason === null) {
    return null;
  }
  const trimmed = reason.trim();
  if (trimmed === '') {
    return null;
  }
  return [...trimmed].slice(0, FEEDBACK_REASON_MAX).join('');
}

export async function applyFeedback(
  client: SupabaseClient,
  input: { commentId: string; myId: string; action: FeedbackAction },
): Promise<void> {
  const { commentId, myId, action } = input;
  if (action.kind === 'insert') {
    const { error } = await client
      .from('message_feedback')
      .insert({ comment_id: commentId, grade: action.grade, reason: null });
    if (error) {
      throw new Error(error.message);
    }
    return;
  }
  if (action.kind === 'update') {
    // Changing the thumb clears the old reason: it explained the other grade.
    const { error } = await client
      .from('message_feedback')
      .update({ grade: action.grade, reason: null })
      .eq('comment_id', commentId)
      .eq('user_id', myId);
    if (error) {
      throw new Error(error.message);
    }
    return;
  }
  const { error } = await client
    .from('message_feedback')
    .delete()
    .eq('comment_id', commentId)
    .eq('user_id', myId);
  if (error) {
    throw new Error(error.message);
  }
}

/** The optional "What was wrong?" line on a thumbs down. */
export async function saveFeedbackReason(
  client: SupabaseClient,
  input: { commentId: string; myId: string; reason: string },
): Promise<void> {
  const { error } = await client
    .from('message_feedback')
    .update({ reason: cleanReason(input.reason) })
    .eq('comment_id', input.commentId)
    .eq('user_id', input.myId);
  if (error) {
    throw new Error(error.message);
  }
}

export function feedbackErrorMessage(raw: string): string {
  if (raw.includes('row-level security') || raw.includes('permission denied')) {
    return 'You cannot rate this message.';
  }
  return 'That did not save. Try again.';
}
